import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Footer from "../../components/Footer/Footer";
import "./VendorStore.css";

/* Drawn in the same thin-stroke language as the marks on the listing page,
   so a lab with no photograph still reads as part of the set. */
function NoPhotoMark() {
  return (
    <svg viewBox="0 0 40 32" width="34" height="28" aria-hidden="true" focusable="false">
      <rect x="1" y="1" width="38" height="30" rx="3" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <path d="M6 24l8.5-9 5.5 6 4.5-4.5L34 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="14" cy="10.5" r="2.4" fill="none" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  );
}

function SearchMark() {
  return (
    <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true" focusable="false">
      <circle cx="7" cy="7" r="4.6" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="m10.4 10.4 3.1 3.1" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function Chevron({ direction }) {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
      <polyline
        points={direction === "left" ? "10,3 5,8 10,13" : "6,3 11,8 6,13"}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const PAGE_SIZE = 12;

export default function VendorStore() {
  const [vendors, setVendors] = useState([]);
  const [status, setStatus] = useState("loading");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 });

  /* Two values, deliberately. `query` is what the field shows and has to
     update on every keystroke; `search` is what the server has been asked
     for, and lags behind it by the debounce. Driving the request straight
     off the input would fire one per character. */
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(query.trim());
      /* A new search invalidates the page number: page 3 of the old result
         set is meaningless against the new one, and often past its end. */
      setPage(1);
    }, 300);
    return () => clearTimeout(id);
  }, [query]);

  /* The request is tied to a token so a slow earlier response cannot land
     after a newer one and repaint the grid with stale rows - the classic
     out-of-order race when someone types faster than the network. */
  const requestRef = useRef(0);

  useEffect(() => {
    const ticket = ++requestRef.current;
    let live = true;

    setStatus("loading");
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/open/v1/public-vendors`, {
        params: { page, limit: PAGE_SIZE, ...(search ? { search } : {}) },
      })
      .then((res) => {
        if (!live || ticket !== requestRef.current) return;
        const d = res.data || {};
        setVendors(d.vendors || []);
        setMeta({ total: d.total ?? 0, totalPages: d.totalPages ?? 1 });
        setStatus("ready");
      })
      .catch((error) => {
        if (!live || ticket !== requestRef.current) return;
        console.error("Failed to load vendors:", error);
        setStatus("error");
      });

    return () => {
      live = false;
    };
  }, [page, search]);

  const goToPage = (next) => {
    setPage(Math.min(Math.max(next, 1), meta.totalPages));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const showTools = search || meta.total > 0;

  return (
    <>
      <div className="vstore">
        <header className="page-head">
          <div className="page-head-text">
            <h1 className="page-title">Vendor store</h1>
            <p className="page-sub">
              Every laboratory currently listing moulds on Tooth Finder. Open
              one to see its catalogue and get in touch.
            </p>
          </div>
        </header>

        {showTools && (
          <div className="vstore-tools">
            <div className="vstore-search">
              <span className="vstore-search-icon" aria-hidden="true">
                <SearchMark />
              </span>
              <input
                type="search"
                className="vstore-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search labs by name or speciality"
                aria-label="Search laboratories"
              />
            </div>
            <p className="vstore-count">
              {meta.total} {meta.total === 1 ? "laboratory" : "laboratories"}
              {meta.totalPages > 1 && (
                <span className="vstore-count-page">
                  {" "}
                  · page {page} of {meta.totalPages}
                </span>
              )}
            </p>
          </div>
        )}

        {status === "loading" && (
          /* Mirrors the card grid so the page does not jump when data
             lands. */
          <ul className="vstore-grid">
            {Array.from({ length: 6 }).map((_, i) => (
              <li key={i}>
                <div className="vstore-card vstore-card-skeleton" />
              </li>
            ))}
          </ul>
        )}

        {status === "error" && (
          <div className="vstore-state">
            <p className="vstore-state-title">Could not load the directory</p>
            <p className="vstore-state-text">
              The request did not come back. Reload the page to try again.
            </p>
          </div>
        )}

        {status === "ready" &&
          (vendors.length > 0 ? (
            <>
              <ul className="vstore-grid">
                {vendors.map((v) => (
                  <li key={v._id}>
                    <Link className="vstore-card" to={`/vendor/${v.slug}`}>
                      <span className="vstore-figure">
                        {v.coverImage ? (
                          <img src={v.coverImage} alt="" loading="lazy" />
                        ) : (
                          <span className="vstore-figure-empty">
                            <NoPhotoMark />
                          </span>
                        )}
                      </span>

                      <span className="vstore-body">
                        <span className="vstore-name">{v.name}</span>
                        {v.category && (
                          <span className="vstore-category">{v.category}</span>
                        )}
                        {v.description && (
                          <span className="vstore-desc">{v.description}</span>
                        )}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>

              {meta.totalPages > 1 && (
                <nav className="vstore-pager" aria-label="Directory pages">
                  <button
                    type="button"
                    className="vstore-page-btn"
                    onClick={() => goToPage(page - 1)}
                    disabled={page <= 1}
                    aria-label="Previous page"
                  >
                    <Chevron direction="left" />
                  </button>

                  <span className="vstore-page-status" aria-live="polite">
                    Page {page} of {meta.totalPages}
                  </span>

                  <button
                    type="button"
                    className="vstore-page-btn"
                    onClick={() => goToPage(page + 1)}
                    disabled={page >= meta.totalPages}
                    aria-label="Next page"
                  >
                    <Chevron direction="right" />
                  </button>
                </nav>
              )}
            </>
          ) : (
            <div className="vstore-state">
              <p className="vstore-state-title">
                {search ? "No labs match that search" : "No labs listed yet"}
              </p>
              <p className="vstore-state-text">
                {search
                  ? "Try a different name or speciality."
                  : "Laboratories appear here once they are approved and listing."}
              </p>
              {search && (
                <button
                  type="button"
                  className="vstore-clear"
                  onClick={() => setQuery("")}
                >
                  Clear search
                </button>
              )}
            </div>
          ))}
      </div>
      <Footer />
    </>
  );
}
